import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Trash2,
  Play,
  ChevronRight,
  ChevronDown,
  FileCode,
  Folder,
  FolderOpen,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from '@forgenetwork/ui';
import { CodeViewer } from '@/components/code';
import { api } from '@/lib/api';
import { useToast } from '@/shared/hooks/useToast';
import type { Repository } from '@/components/repository';

export const Route = createFileRoute('/_authenticated/repositories/$repoId')({
  component: RepositoryDetailPage,
});

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
  size?: number;
}

interface RepositoryDetail extends Repository {
  files: FileNode[];
  analysisResults?: {
    totalLines: number;
    totalFiles: number;
    languages: Record<string, number>;
    features?: string[];
  };
}

interface TreeNodeProps {
  node: FileNode;
  level?: number;
  onFileClick?: (filePath: string) => void;
}

function TreeNode({ node, level = 0, onFileClick }: TreeNodeProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = React.useState(level < 2);

  if (node.type === 'file') {
    return (
      <div
        className="flex items-center gap-2 py-1 px-2 hover:bg-accent rounded-sm cursor-pointer"
        style={{ paddingLeft: `${level * 1.5 + 0.5}rem` } as React.CSSProperties}
        onClick={() => onFileClick?.(node.path)}
      >
        <FileCode className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm truncate">{node.name}</span>
        {node.size && (
          <span className="text-xs text-muted-foreground ml-auto">
            {(node.size / 1024).toFixed(1)} KB
          </span>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-2 hover:bg-accent rounded-sm cursor-pointer"
        style={{ paddingLeft: `${level * 1.5 + 0.5}rem` } as React.CSSProperties}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-primary flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-primary flex-shrink-0" />
        )}
        <span className="text-sm font-medium truncate">{node.name}</span>
        {node.children && (
          <span className="text-xs text-muted-foreground ml-auto">
            {node.children.length} items
          </span>
        )}
      </div>
      {isExpanded && node.children && (
        <div>
          {node.children.map((child, index) => (
            <TreeNode key={`${child.path}-${index}`} node={child} level={level + 1} onFileClick={onFileClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function RepositoryDetailPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = React.useState<string | null>(null);

  const {
    data: repository,
    isLoading,
    error,
  } = useQuery<RepositoryDetail>({
    queryKey: ['repository', repoId],
    queryFn: async () => {
      return await api.get<RepositoryDetail>(`/repositories/${repoId}`);
    },
  });

  const {
    data: fileContent,
    isLoading: isLoadingFile,
    error: fileError,
  } = useQuery<{ path: string; content: string }>({
    queryKey: ['file-content', repoId, selectedFile],
    queryFn: async () => {
      if (!selectedFile) throw new Error('No file selected');
      return await api.get<{ path: string; content: string }>(
        `/repositories/${repoId}/files/${selectedFile}`
      );
    },
    enabled: !!selectedFile,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await api.delete(`/repositories/${repoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repositories'] });
      toast({
        title: 'Success',
        description: 'Repository deleted successfully',
      });
      navigate({ to: '/repositories' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete repository',
        variant: 'destructive',
      });
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/repositories/${repoId}/analyze`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repository', repoId] });
      toast({
        title: 'Analysis Started',
        description: 'Repository analysis has been initiated',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start analysis',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = (): void => {
    if (confirm('Are you sure you want to delete this repository? This action cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  const handleAnalyze = (): void => {
    analyzeMutation.mutate();
  };

  const getStatusBadge = (status: Repository['analysisStatus']): React.JSX.Element => {
    switch (status) {
      case 'complete':
        return <Badge variant="success">Complete</Badge>;
      case 'analyzing':
        return <Badge variant="warning">Analyzing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !repository) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Repository not found'}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: '/repositories' })}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Repositories
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: '/repositories' })}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{repository.name}</h1>
              <p className="text-muted-foreground mt-1">
                Created {new Date(repository.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleAnalyze}
              disabled={analyzeMutation.isPending || repository.analysisStatus === 'analyzing'}
            >
              {analyzeMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Analysis
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent>
              {getStatusBadge(repository.analysisStatus)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Files</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{repository.fileCount}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total Lines</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {repository.analysisResults?.totalLines.toLocaleString() || 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <Card className="lg:col-span-4">
            <CardHeader>
              <CardTitle>File Tree</CardTitle>
              <CardDescription>
                Browse repository contents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 max-h-[600px] overflow-y-auto">
                {repository.files && repository.files.length > 0 ? (
                  repository.files.map((node, index) => (
                    <TreeNode
                      key={`${node.path}-${index}`}
                      node={node}
                      onFileClick={setSelectedFile}
                    />
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No files found
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>File Viewer</CardTitle>
                  <CardDescription>
                    {selectedFile
                      ? 'Viewing file contents'
                      : 'Select a file to view its contents'}
                  </CardDescription>
                </div>
                {selectedFile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedFile(null)}
                    title="Close file"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedFile ? (
                <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
                  <div className="text-center">
                    <FileCode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">
                      Click on a file in the tree to view its contents
                    </p>
                  </div>
                </div>
              ) : isLoadingFile ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : fileError ? (
                <div className="flex items-center justify-center min-h-[400px] text-destructive">
                  <div className="text-center">
                    <p className="text-sm">
                      {fileError instanceof Error
                        ? fileError.message
                        : 'Failed to load file content'}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => setSelectedFile(null)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ) : fileContent ? (
                <CodeViewer
                  content={fileContent.content}
                  filePath={fileContent.path}
                  showLineNumbers={true}
                />
              ) : null}
            </CardContent>
          </Card>
        </div>

        {repository.analysisResults && (
          <Card>
            <CardHeader>
              <CardTitle>Analysis Results</CardTitle>
              <CardDescription>
                Code analysis summary
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {repository.analysisResults.languages && (
                <div>
                  <h2 className="text-sm font-medium mb-3">Languages</h2>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(repository.analysisResults.languages).map(
                      ([lang, count]) => (
                        <div
                          key={lang}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <span className="font-medium">{lang}</span>
                          <span className="text-sm text-muted-foreground">
                            {count} files
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {repository.analysisResults.features && (
                <div>
                  <h3 className="text-sm font-medium mb-3">Features Detected</h3>
                  <div className="flex flex-wrap gap-2">
                    {repository.analysisResults.features.map((feature) => (
                      <Badge key={feature} variant="secondary">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
